import { RefObject, useEffect, useRef } from 'react';

/**
 * A custom hook that attaches a 'scrollend' event listener to a referenced element,
 * with a fallback for browsers that do not support it. This ensures the callback
 * fires reliably when a user-initiated scroll action has completed.
 *
 * @param ref A React ref object pointing to the HTML element to observe.
 * @param callback The function to execute when the scroll has ended.
 * @param delay The timeout delay in milliseconds for the fallback mechanism (default: 150ms).
 */
const useOnScrollEnd = (
  ref: RefObject<HTMLElement | null>,
  callback: (event: Event) => void,
  delay = 100
): void => {
  useEffect(() => {
    let scrollTimeout: number | null = null;
    const element = ref.current;
    const handleScroll = (event: Event): void => {
      // Clear the previous timeout on each scroll event.
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      // Set a new timeout. The callback will only run after the user stops scrolling.
      scrollTimeout = window.setTimeout(() => {
        callback(event);
      }, delay);
    };

    // Safari Webkit does not support scrollend events directly. We check here if the browser window has `onscrollend` defined
    // If it does, we can use the scrollend event, otherwise we use a timeout workaround that triggers on the scroll event
    const isScrollEndSupported = 'onscrollend' in window;
    const onScrollEnd = isScrollEndSupported ? callback : handleScroll;
    const eventType = isScrollEndSupported ? 'scrollend' : 'scroll';

    // ~ Event handling setup
    element?.addEventListener(eventType, onScrollEnd);

    return () => {
      element?.removeEventListener(eventType, onScrollEnd);
      // Cleanup: remove the listener and clear any pending timeout.
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [ref, callback, delay]);
};

export default useOnScrollEnd;
